import React, { useState } from 'react';
import { FileSpreadsheet, Loader2, Download, Check } from 'lucide-react';

interface ExcelExportButtonProps {
  title: string;
  content: string;
  fileName?: string;
  className?: string;
  variant?: 'primary' | 'emerald' | 'compact' | 'secondary';
  isUk?: boolean;
}

export default function ExcelExportButton({
  title,
  content,
  fileName,
  className = '',
  variant = 'emerald',
  isUk = true
}: ExcelExportButtonProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleDownloadExcel = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!content || downloading) return;

    setDownloading(true);
    try {
      const cleanName = (fileName || title || 'rozrakhunok')
        .replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, '_')
        .slice(0, 50);

      const response = await fetch('/api/generate-xlsx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: content,
          fileName: cleanName,
          documentTitle: title
        })
      });

      if (!response.ok) {
        throw new Error(isUk ? 'Помилка при генерації Excel файлу' : 'Failed to generate Excel file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cleanName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 3000);
    } catch (err: any) {
      console.error('Excel Download Error:', err);
      alert(err.message || (isUk ? 'Не вдалося сформувати Excel таблицю' : 'Failed to generate Excel file'));
    } finally {
      setDownloading(false);
    }
  };

  let btnClasses = 'px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95 select-none ';

  if (variant === 'primary') {
    btnClasses += 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-600/20';
  } else if (variant === 'compact') {
    btnClasses = 'px-2.5 py-1 text-[10px] font-bold font-mono text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded border border-emerald-300 transition-colors cursor-pointer uppercase flex items-center gap-1';
  } else if (variant === 'secondary') {
    btnClasses += 'bg-slate-800 hover:bg-slate-700 text-emerald-400 border border-slate-700 hover:border-emerald-500/50';
  } else {
    btnClasses += 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-600/20';
  }

  return (
    <button
      type="button"
      onClick={handleDownloadExcel}
      disabled={downloading}
      title={isUk ? 'Завантажити таблицю Excel (.xlsx) із живими формулами (SUM, SUMIF, PRODUCT)' : 'Download Excel (.xlsx) with live dynamic formulas'}
      className={`${btnClasses} ${className} ${downloading ? 'opacity-75 cursor-wait' : ''}`}
    >
      {downloading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-300" />
      ) : downloaded ? (
        <Check className="w-3.5 h-3.5 text-white" />
      ) : (
        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-200" />
      )}
      <span>
        {downloading
          ? (isUk ? 'Формування Excel...' : 'Generating Excel...')
          : downloaded
          ? (isUk ? 'Завантажено!' : 'Downloaded!')
          : (isUk ? 'Excel (.xlsx)' : 'Excel (.xlsx)')}
      </span>
    </button>
  );
}
